import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CrudList, CrudStyles } from '../shared/Crud';
import { TabBar, TabButton, TabContent, ActionBtn } from '../Deliveries/DeliveriesList.styles';
import { EyeIcon } from '../Layout/icons';
import legalPersonService from '../../services/legalPersonService';
import physicalPersonService from '../../services/physicalPersonService';
import { parseCustomerListSearchQuery } from '../../utils/customerSearchFilter';
import { buildPhysicalPersonListFilters } from '../../utils/physicalPersonListParams';
import { LEGAL_PERSON_LIST_COLUMNS } from '../shared/legalPersonListColumns';
import { PHYSICAL_PERSON_LIST_COLUMNS } from '../shared/physicalPersonListColumns';
import LegalPersonSearchFilter from '../shared/LegalPersonSearchFilter';
import LegalPersonForm from './LegalPersonForm';
import LegalPersonDetailView from './LegalPersonDetailView';
import PhysicalPersonForm from './PhysicalPersonForm';
import PhysicalPersonDetailView from './PhysicalPersonDetailView';

const { Button: CrudOutlineButton } = CrudStyles;

const CLIENT_TABS = [
    { id: 'pessoa-juridica', label: 'Pessoa jurídica', visible: true },
    { id: 'pessoa-fisica', label: 'Pessoa física', visible: true },
];

function ClientDetailsAction({ onClick }) {
    return (
        <ActionBtn type="button" onClick={onClick} title="Detalhes">
            <EyeIcon />
            Detalhes
        </ActionBtn>
    );
}

/**
 * Tela de clientes (empresa de transporte). Abas PJ e PF.
 */
export default function Clients() {
    const [view, setView] = useState('list');
    const [activeTabId, setActiveTabId] = useState('pessoa-juridica');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const isInitialMount = useRef(true);
    const [listVersion, setListVersion] = useState(0);
    const [detailId, setDetailId] = useState(null);

    const visibleTabs = useMemo(() => CLIENT_TABS.filter((t) => t.visible), []);
    const isPhysicalTab = activeTabId === 'pessoa-fisica';

    useEffect(() => {
        const isActiveVisible = visibleTabs.some((t) => t.id === activeTabId);
        if (!isActiveVisible && visibleTabs.length > 0) {
            setActiveTabId(visibleTabs[0].id);
        }
    }, [visibleTabs, activeTabId]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setSearchQuery(localSearchQuery);
            setPagination((prev) => ({ ...prev, page: 0 }));
        }, 750);
        return () => clearTimeout(timer);
    }, [localSearchQuery]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setListError('');
            try {
                let result;
                if (isPhysicalTab) {
                    const pfFilters = buildPhysicalPersonListFilters(searchQuery);
                    result = await physicalPersonService.list({
                        page: pagination.page,
                        size: 10,
                        sort: 'name,asc',
                        ...pfFilters,
                    });
                } else {
                    const { name, document } = parseCustomerListSearchQuery(searchQuery);
                    result = await legalPersonService.list({
                        page: pagination.page,
                        size: 10,
                        sort: 'name,asc',
                        ...(name ? { name } : {}),
                        ...(document ? { document } : {}),
                    });
                }
                if (cancelled) return;
                const content = result.content || [];
                const totalPages = content.length === 0 ? 0 : result.page?.totalPages ?? 1;
                const pageNumber =
                    totalPages === 0 ? 0 : Math.min(result.page?.number ?? 0, Math.max(0, totalPages - 1));

                setData(content);
                setPagination((prev) => ({
                    ...prev,
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                }));
            } catch {
                if (!cancelled) {
                    setData([]);
                    setListError(
                        isPhysicalTab
                            ? 'Não foi possível carregar a listagem de pessoas físicas.'
                            : 'Não foi possível carregar a listagem de pessoas jurídicas.',
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [activeTabId, isPhysicalTab, pagination.page, searchQuery, listVersion]);

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleTabChange = (tabId) => {
        setActiveTabId(tabId);
        setView('list');
        setDetailId(null);
        setLocalSearchQuery('');
        setSearchQuery('');
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const tabBar = (
        <TabBar>
            {visibleTabs.map((tab) => (
                <TabButton
                    key={tab.id}
                    type="button"
                    $active={activeTabId === tab.id}
                    onClick={() => handleTabChange(tab.id)}
                >
                    {tab.label}
                </TabButton>
            ))}
        </TabBar>
    );

    if (view === 'create' && activeTabId === 'pessoa-juridica') {
        return (
            <>
                {tabBar}
                <TabContent>
                    <LegalPersonForm
                        onCancel={() => setView('list')}
                        onSuccess={() => {
                            setView('list');
                            setListVersion((v) => v + 1);
                        }}
                    />
                </TabContent>
            </>
        );
    }

    if (view === 'create' && activeTabId === 'pessoa-fisica') {
        return (
            <>
                {tabBar}
                <TabContent>
                    <PhysicalPersonForm
                        onCancel={() => setView('list')}
                        onSuccess={() => {
                            setView('list');
                            setListVersion((v) => v + 1);
                        }}
                    />
                </TabContent>
            </>
        );
    }

    if (view === 'detail' && activeTabId === 'pessoa-juridica' && detailId != null) {
        return (
            <>
                {tabBar}
                <TabContent>
                    <LegalPersonDetailView
                        legalPersonId={detailId}
                        onBack={() => {
                            setView('list');
                            setDetailId(null);
                        }}
                    />
                </TabContent>
            </>
        );
    }

    if (view === 'detail' && activeTabId === 'pessoa-fisica' && detailId != null) {
        return (
            <>
                {tabBar}
                <TabContent>
                    <PhysicalPersonDetailView
                        customerId={detailId}
                        onBack={() => {
                            setView('list');
                            setDetailId(null);
                        }}
                    />
                </TabContent>
            </>
        );
    }

    return (
        <>
            {tabBar}
            <TabContent>
                <CrudList
                    title={isPhysicalTab ? 'Pessoa física' : 'Pessoa jurídica'}
                    newButtonLabel={isPhysicalTab ? 'Nova pessoa física' : 'Nova pessoa jurídica'}
                    onNew={() => setView('create')}
                    tableMinWidth="800px"
                    columns={isPhysicalTab ? PHYSICAL_PERSON_LIST_COLUMNS : LEGAL_PERSON_LIST_COLUMNS}
                    data={data}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                    actions={(item) => (
                        <ClientDetailsAction
                            onClick={() => {
                                if (item?.id != null) {
                                    setDetailId(item.id);
                                    setView('detail');
                                }
                            }}
                        />
                    )}
                    filters={
                        <>
                            <LegalPersonSearchFilter
                                idPrefix={isPhysicalTab ? 'clients-pf' : 'clients-pj'}
                                value={localSearchQuery}
                                onChange={setLocalSearchQuery}
                                label="Buscar"
                                placeholder={
                                    isPhysicalTab
                                        ? 'Nome ou CPF'
                                        : 'Nome da empresa ou CPF / CNPJ'
                                }
                                hint={
                                    isPhysicalTab
                                        ? 'Com espaço ou só letras, filtra por nome; CPF (com dígito), por documento. Ordenação: nome (A → Z).'
                                        : undefined
                                }
                            />
                            {listError && (
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }} role="alert">
                                        {listError}
                                    </p>
                                    <CrudOutlineButton
                                        type="button"
                                        variant="outline"
                                        onClick={() => setListVersion((v) => v + 1)}
                                    >
                                        Tentar novamente
                                    </CrudOutlineButton>
                                </div>
                            )}
                        </>
                    }
                />
            </TabContent>
        </>
    );
}
